/**
 * Error observability for the browser SPA — Sentry/GlitchTip.
 *
 * ADDITIVE + APP-EXCLUSIVE: gated on a DSN. No DSN ⇒ inert no-op, safe to ship before
 * the `sentry.compass-platform.dev` service exists.
 *
 * DSN SOURCE (browser): a Sentry DSN is a PUBLIC ingest key, designed to be embedded
 * in client code — it is NOT a secret. We read it from a `<meta name="sentry-dsn">`
 * tag in the document head. When the tag is absent (today) this no-ops; wiring the DSN
 * is a one-line follow-up (the BFF injects the meta tag, or it is baked into
 * `index.html`) that needs NO code change here.
 */

import * as Sentry from "@sentry/browser"

let enabled = false

/** Read the public DSN from `<meta name="sentry-dsn" content="…">`, if present. */
function readDsn(): string | undefined {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')
  const dsn = meta?.content?.trim()
  return dsn ? dsn : undefined
}

/** Initialise browser Sentry ONCE. Idempotent, no-op when no DSN meta tag is present. */
export function initBrowserSentry(): void {
  if (enabled) return
  const dsn = readDsn()
  if (!dsn) return

  const env = document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')?.content?.trim()

  Sentry.init({
    dsn,
    environment: env || "unknown",
    // Error tracking only — no perf tracing / session replay (keeps the browser bundle
    // + GlitchTip event volume small). Sentry auto-hooks window.onerror +
    // unhandledrejection, so uncaught SPA errors are captured without call-site changes.
    tracesSampleRate: 0,
    sendDefaultPii: false,
  })
  enabled = true
}

/** Capture an exception from the SPA. No-op until `initBrowserSentry` wires a DSN. */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!enabled) return
  Sentry.captureException(error, context ? { extra: context } : undefined)
}
